'use client';

/**
 * @fileoverview Global Locus mount — surface + session gating (FRE-1229 /
 * FRE-1230, PRD §6.3/§6.4).
 *
 * Mounted exactly once from `app/providers.tsx` (the single Next.js root
 * shared by every route group), this component composes the gates that decide
 * which Locus presence — if any — renders:
 *
 *   1. **Route group** — the PRD §6.3 surface matrix via
 *      {@link resolveLocusMount}; `(admin)` never mounts regardless of
 *      session or flag, and locations outside the matrix (fallback hosts,
 *      auth pages) get nothing.
 *   2. **Session** — Locus conversation is signed-in only (PRD §6.4).
 *      - **Signed-in** → the live runtime, additionally gated by the
 *        `locus-runtime` feature flag evaluated **per route group**
 *        (FRE-1231's staged-rollout lever: the flag's JSON payload
 *        `{"surfaces":["app",...]}` decides which groups are live, in the
 *        PRD §9 order `(app)` → `(dynamic)` → `(city)`). Evaluation is
 *        **server-side** via `useLocusRuntimeFlag` → `/api/feature-flags/locus`
 *        (FRE-1270), so analytics-consent denial cannot hide the feature.
 *        Strict default-off: flag undefined / unavailable / `false` / payload
 *        missing, malformed, or lacking this route group / BFF error ⇒
 *        nothing mounts. `develop` deploys straight to production, so this is
 *        what keeps the conversation invisible until the staged rollout flips
 *        it on.
 *      - **Signed-out** → the honest-door teaser launcher (WEB-624 / PL-04;
 *        formerly FRE-1230 fake-door redirect), deliberately **unflagged**:
 *        a locked glass O that opens a gated teaser sheet — never a live
 *        conversation, never `/api/locus`. It renders only on the `(city)` /
 *        `(apex)` / `(dynamic)` rows of the matrix — the `(app)` row is
 *        auth-gated at the proxy (signed-out 307s to `/login`), so a
 *        signed-out render there is a mid-redirect flicker and gets nothing.
 *
 * CWV posture (FRE-1231 §8): both branches load through `next/dynamic` with
 * `ssr: false`, so no Locus JS or CSS ships in any page's initial bundle. The
 * signed-in chunk carries the full runtime graph (launcher, panel, reply
 * blocks, `@loci/api` hooks); the signed-out chunk carries only the launcher
 * button + `styles/locus.css`. Neither imports the other, so a signed-out
 * visitor can never fetch the runtime graph.
 */

import dynamicImport from 'next/dynamic';
import { usePathname } from 'next/navigation';
import type { JSX } from 'react';

import { useAuth } from '@/contexts/AuthContext';
import { useCityContext, useCitySurface } from '@/lib/city-context.client';
import {
  isLocusRuntimeEvaluationEnabledFor,
  useLocusRuntimeFlag,
} from '@/lib/featureFlags/locusRuntime';

import { resolveLocusMount } from './locusMountGate';

// Deep imports (not the `@/components/overlay/locus` barrel) so each lazy
// chunk carries only its own graph, and `ssr: false` because both branches are
// pure client interaction — there is nothing meaningful to server-render for
// a dormant launcher, and skipping SSR keeps them out of the HTML entirely.
const LocusRuntimeLazy = dynamicImport(
  () => import('./LocusRuntime').then((mod) => ({ default: mod.LocusRuntime })),
  { ssr: false },
);

const LocusSignedOutCtaLazy = dynamicImport(
  () => import('./LocusSignedOutCta').then((mod) => ({ default: mod.LocusSignedOutCta })),
  { ssr: false },
);

/**
 * The global, surface-gated Locus mount. Signed-in visitors on eligible route
 * groups get the flag-gated live runtime; signed-out visitors on
 * `(city)` / `(apex)` / `(dynamic)` get the unflagged sign-up CTA; everything
 * else renders `null` — and downloads nothing.
 */
export function LocusGlobalMount(): JSX.Element | null {
  const { user } = useAuth();
  const pathname = usePathname();
  const citySurface = useCitySurface();
  const city = useCityContext();

  // Route-group resolution runs before the flag check because the FRE-1231
  // staged rollout is evaluated *per route group*: the `locus-runtime` flag's
  // JSON payload (`{"surfaces":["app",...]}`) decides which groups are live,
  // so `(app)` can roll out ahead of `(dynamic)` ahead of `(city)`.
  // `resolveLocusMount` is pure, so calling it pre-hooks is safe. The flag
  // hook itself does not take a route group — it returns the full evaluation
  // and we project per group below (FRE-1270).
  const decision = resolveLocusMount(pathname ?? '/', citySurface);
  const locusFlag = useLocusRuntimeFlag();
  const flagEnabled = isLocusRuntimeEvaluationEnabledFor(locusFlag, decision.routeGroup);

  // Gate 1 — route group (PRD §6.3 matrix). `(admin)` and off-matrix
  // locations exit here for every session state.
  if (decision.surface === null) return null;

  // Gate 2 — session. A resolved user id is the same bar AppHeader's authed
  // chrome uses (`isAuthed = hasSession && userId !== null`); like AppHeader's
  // Sign-in pill, the signed-out branch may flash briefly while `/auth/me`
  // resolves for a returning session — accepted product behavior (WEB-333).
  const userId = typeof user?.id === 'string' && user.id.length > 0 ? user.id : null;

  if (userId === null) {
    // Signed-out (FRE-1230) — sign-up CTA, unflagged (see fileoverview). The
    // `(app)` route group is proxy-auth-gated, so signed-out there is only a
    // transient mid-redirect state; render nothing rather than a CTA that
    // would flicker for one frame before the 307 lands.
    if (decision.routeGroup === 'app') return null;
    return <LocusSignedOutCtaLazy />;
  }

  // Gate 3 — staged-rollout flag, signed-in only: `locus-runtime` must be on
  // AND its payload must list this route group (FRE-1231). The hook starts
  // `false` on SSR and first client paint, so hydration always agrees on
  // `null`.
  if (!flagEnabled) return null;

  return (
    <LocusRuntimeLazy
      surface={decision.surface}
      userId={userId}
      cityCode={city?.code ?? null}
      // WEB-623: app surface uses the bottom-sheet form behind the dock FAB;
      // the corner launcher is suppressed so `right:24px; bottom:24px` stays empty.
      form={decision.surface === 'app' ? 'sheet' : 'corner'}
      externalLauncher={decision.surface === 'app'}
    />
  );
}
